import React, { useEffect, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Drawer, IconButton, Typography, Box, FormControl, TextField, Button } from '@mui/material';
import { useActivateBusiness } from 'api/queries/useActivateBusiness';
import type { Business } from 'api/queries/useIndividualBusiness';
import './ActivateBusinessModal.scss';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LW_SYNTHETIC_EMAIL_DOMAIN = '@laundroworks.internal';
const LW_SYNTHETIC_OWNER_FIRSTNAME = 'Owner';

interface ActivateBusinessModalProps {
  isOpen: boolean;
  onClose: () => void;
  business: Business;
  onSaveSuccess?: () => void;
}

export const ActivateBusinessModal: React.FC<ActivateBusinessModalProps> = ({
  isOpen,
  onClose,
  business,
  onSaveSuccess,
}) => {
  const [businessName, setBusinessName] = useState('');
  const [ownerFirstname, setOwnerFirstname] = useState('');
  const [ownerLastname, setOwnerLastname] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');

  useEffect(() => {
    if (!isOpen) return;

    const user = business.user;
    const isSyntheticEmail = Boolean(user?.email?.includes(LW_SYNTHETIC_EMAIL_DOMAIN));
    const isSyntheticFirstname = user?.firstname === LW_SYNTHETIC_OWNER_FIRSTNAME;

    setBusinessName(business.name ?? '');
    setOwnerFirstname(isSyntheticFirstname ? '' : user?.firstname ?? '');
    setOwnerLastname(user?.lastname ?? '');
    setOwnerEmail(isSyntheticEmail ? '' : user?.email ?? '');
    setOwnerPhone(user?.phone ?? '');
  }, [isOpen, business]);

  const { mutate: activateBusiness, isLoading: isSaving } = useActivateBusiness({
    successCallback: () => {
      onSaveSuccess?.();
      onClose();
    },
  });

  const isValid =
    businessName.trim().length > 0 &&
    ownerFirstname.trim().length > 0 &&
    ownerLastname.trim().length > 0 &&
    ownerPhone.trim().length > 0 &&
    EMAIL_PATTERN.test(ownerEmail.trim());

  const handleActivate = () => {
    activateBusiness({
      businessId: business.id,
      businessName: businessName.trim(),
      ownerFirstname: ownerFirstname.trim(),
      ownerLastname: ownerLastname.trim(),
      ownerEmail: ownerEmail.trim(),
      ownerPhone: ownerPhone.trim(),
    });
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      classes={{
        paper: 'activate-business-panel',
      }}
    >
      <div className="activate-business-panel-container">
        <div className="activate-business-panel-header">
          <Typography variant="h5" className="activate-business-panel-title">
            Activate Business
          </Typography>
          <IconButton onClick={onClose} className="activate-business-panel-close">
            <CloseIcon />
          </IconButton>
        </div>
        <div className="activate-business-panel-content">
          <Box className="activate-business-section">
            <Typography variant="h6" className="section-title">
              Business Information
            </Typography>
            <FormControl fullWidth className="activate-business-form-control">
              <TextField
                variant="outlined"
                label="Name"
                value={businessName}
                onChange={(event) => setBusinessName(event.target.value)}
              />
            </FormControl>
          </Box>
          <Box className="activate-business-section">
            <Typography variant="h6" className="section-title">
              Business Owner Information
            </Typography>
            <FormControl fullWidth className="activate-business-form-control">
              <TextField
                variant="outlined"
                label="First Name"
                value={ownerFirstname}
                onChange={(event) => setOwnerFirstname(event.target.value)}
              />
            </FormControl>
            <FormControl fullWidth className="activate-business-form-control">
              <TextField
                variant="outlined"
                label="Last Name"
                value={ownerLastname}
                onChange={(event) => setOwnerLastname(event.target.value)}
              />
            </FormControl>
            <FormControl fullWidth className="activate-business-form-control">
              <TextField
                variant="outlined"
                label="Email"
                value={ownerEmail}
                onChange={(event) => setOwnerEmail(event.target.value)}
              />
            </FormControl>
            <FormControl fullWidth className="activate-business-form-control">
              <TextField
                variant="outlined"
                label="Phone Number"
                value={ownerPhone}
                onChange={(event) => setOwnerPhone(event.target.value)}
              />
            </FormControl>
          </Box>
        </div>
        <div className="activate-business-panel-footer">
          <Button variant="outlined" color="primary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleActivate}
            disabled={!isValid || isSaving}
          >
            Activate
          </Button>
        </div>
      </div>
    </Drawer>
  );
};
